/* eslint-disable */
import React, { useState } from 'react';
import PhoneScannerScreen from '../PhoneScannerScreen';

const stripSerial=(s)=>{const m=s.match(/\/sn\/([^/?#]+)/);if(m)return m[1].trim();const m2=s.match(/sn([A-Za-z0-9]+)$/i);if(m2)return m2[1].trim();if(s.startsWith("http")){const p=s.replace(/\/$/,"").split("/");return p[p.length-1].trim();}return s;};
const generateAnonLabel = () => 'DEV-' + Math.random().toString(36).slice(2, 6).toUpperCase();

/**
 * SwapSheet — 2-step swap: scan returning device, then scan replacement.
 * Transactional: only commits when step 2 is confirmed.
 */
export default function SwapSheet({ st, setSt, shiftId, account, audit, onBack, onSuccess, prefilledAllocationId }) {
  const shift = (st.shifts || []).find(s => s.id === shiftId);
  const isAnonymous = shift?.anonymousMode === true;

  const prefilled = prefilledAllocationId ? (() => {
    const a = (st.allocations || []).find(x => x.id === prefilledAllocationId && x.shiftId === shiftId && x.status === 'out');
    if (!a) return null;
    const d = (st.devices || []).find(x => x.id === a.deviceId);
    return d ? { alloc: a, device: d } : null;
  })() : null;

  const [step, setStep] = useState(prefilled ? 2 : 1);
  const [returningAlloc, setReturningAlloc] = useState(prefilled ? prefilled.alloc : null);
  const [returningDevice, setReturningDevice] = useState(prefilled ? prefilled.device : null);
  const [replacementDevice, setReplacementDevice] = useState(null);
  const [manualSerial1, setManualSerial1] = useState('');
  const [manualSerial2, setManualSerial2] = useState('');
  const [error, setError] = useState(null);

  const handleStep1Scan = (code) => {
    const serial = stripSerial(code);
    const device = (st.devices || []).find(d =>
      d.serial === serial || d.serial === code || d.id === serial ||
      (d.serial && d.serial.toLowerCase().includes(serial.toLowerCase()))
    );
    if (!device) { setError(`Device not found: ${serial}`); return; }

    const alloc = (st.allocations || []).find(a => a.deviceId === device.id && a.shiftId === shiftId && a.status === 'out');
    if (!alloc) { setError(`No open allocation for ${serial} on this shift.`); return; }

    setError(null);
    setReturningAlloc(alloc);
    setReturningDevice(device);
    setStep(2);
  };

  const handleStep2Scan = (code) => {
    const serial = stripSerial(code);
    const device = (st.devices || []).find(d =>
      d.serial === serial || d.serial === code || d.id === serial ||
      (d.serial && d.serial.toLowerCase().includes(serial.toLowerCase()))
    );
    if (!device) { setError(`Device not found: ${serial}`); return; }

    // Check if replacement is already out
    const existingOut = (st.allocations || []).find(a => a.deviceId === device.id && a.status === 'out');
    if (existingOut) { setError(`Device ${serial} is already out. Cannot use as replacement.`); return; }

    if (device.id === returningDevice.id) { setError('Cannot swap a device with itself.'); return; }

    setError(null);
    setReplacementDevice(device);
  };

  const handleConfirm = () => {
    if (!returningAlloc || !replacementDevice) return;
    const now = new Date().toISOString();
    const newAllocId = 'alloc-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);

    setSt(prev => ({
      ...prev,
      allocations: [
        ...prev.allocations.map(a =>
          a.id === returningAlloc.id
            ? { ...a, status: 'returned', inAt: now, swappedToAllocationId: newAllocId, notes: (a.notes ? a.notes + ' | ' : '') + 'Swapped → ' + (isAnonymous ? 'replacement' : replacementDevice.serial) }
            : a
        ),
        {
          id: newAllocId,
          shiftId,
          deviceId: replacementDevice.id,
          role: returningAlloc.role,
          operativeName: returningAlloc.operativeName,
          operativePhone: returningAlloc.operativePhone,
          outAt: now,
          inAt: null,
          status: 'out',
          notes: 'Swapped from ' + (isAnonymous ? (returningAlloc.anonDeviceLabel || 'previous') : returningDevice.serial),
          anonDeviceLabel: isAnonymous ? generateAnonLabel() : null,
          swappedFromAllocationId: returningAlloc.id,
          swappedToAllocationId: null,
          carriedFromAllocationId: null,
          flaggedByUserId: null,
          flaggedAt: null,
          recoveredAt: null,
          createdByUserId: account?.username || 'unknown',
          createdAt: now,
        },
      ],
      devices: prev.devices.map(d => {
        if (d.id === returningDevice.id) return { ...d, availability: 'available' };
        if (d.id === replacementDevice.id) return { ...d, availability: 'deployed' };
        return d;
      }),
    }));

    audit('allocation_swapped', { shiftId, returningDeviceId: returningDevice.id, replacementDeviceId: replacementDevice.id });

    if (onSuccess) onSuccess({ returningDevice, replacementDevice });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface-app)', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: '50%', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)' }}>Swap device</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>Return failing → issue replacement</div>
        </div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
          <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, ...(step > 1 ? { background: 'rgba(74,222,128,0.12)', border: '1px solid #4ade80', color: '#4ade80' } : { background: '#ff530d', border: '1px solid #ff530d', color: '#fff' }) }}>1</div>
          <div style={{ width: 30, height: 2, background: step > 1 ? '#4ade80' : 'var(--border-default)' }} />
          <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, ...(step === 2 ? { background: '#ff530d', border: '1px solid #ff530d', color: '#fff' } : { background: 'var(--surface-inset)', border: '1px solid var(--border-default)', color: 'var(--text-subtle)' }) }}>2</div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid #f87171', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#f87171', marginBottom: 16 }}>
            {error}
            <button onClick={() => setError(null)} style={{ marginTop: 8, display: 'block', background: 'transparent', border: '1px solid #f87171', borderRadius: 6, color: '#f87171', padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
          </div>
        )}

        {/* Step 1: scan returning device */}
        {step === 1 && !error && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-subtle)', marginBottom: 10 }}>Step 1 · Scan returning device</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input autoFocus value={manualSerial1} onChange={e => setManualSerial1(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && manualSerial1.trim()) handleStep1Scan(manualSerial1.trim()); }}
                placeholder="Enter serial number or scan QR..."
                style={{ flex: 1, padding: '12px 14px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }} />
              <button onClick={() => manualSerial1.trim() && handleStep1Scan(manualSerial1.trim())} disabled={!manualSerial1.trim()}
                style={{ padding: '12px 18px', borderRadius: 10, border: 'none', background: manualSerial1.trim() ? '#ff530d' : 'var(--surface-raised)', color: manualSerial1.trim() ? '#fff' : 'var(--text-subtle)', fontWeight: 600, fontSize: 13, cursor: manualSerial1.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Look up</button>
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 10 }}>or scan with camera</div>
            <div style={{ borderRadius: 14, overflow: 'hidden', background: '#111', border: '1px solid var(--border-default)' }}>
              <PhoneScannerScreen onScan={handleStep1Scan} compact />
            </div>
          </>
        )}

        {/* Step 2: returning device confirmed, scan replacement */}
        {step === 2 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-subtle)', marginBottom: 10 }}>Step 1 · Returning device</div>
            <div style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid #4ade80', borderRadius: 14, padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#4ade80', fontFamily: isAnonymous ? 'monospace' : 'inherit' }}>{returningAlloc?.anonDeviceLabel || returningDevice.serial}</span>
                <span style={{ fontSize: 11, color: 'var(--text-default)', fontWeight: 500 }}>{returningAlloc.role} · {returningAlloc.operativeName || 'Unassigned'}</span>
              </div>
              <div style={{ fontSize: 11, color: '#4ade80' }}>Ready for replacement</div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-subtle)', marginBottom: 10, marginTop: 18 }}>Step 2 · Scan replacement</div>

            {!replacementDevice && !error && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input autoFocus value={manualSerial2} onChange={e => setManualSerial2(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && manualSerial2.trim()) handleStep2Scan(manualSerial2.trim()); }}
                    placeholder="Enter replacement serial..."
                    style={{ flex: 1, padding: '12px 14px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box' }} />
                  <button onClick={() => manualSerial2.trim() && handleStep2Scan(manualSerial2.trim())} disabled={!manualSerial2.trim()}
                    style={{ padding: '12px 18px', borderRadius: 10, border: 'none', background: manualSerial2.trim() ? '#ff530d' : 'var(--surface-raised)', color: manualSerial2.trim() ? '#fff' : 'var(--text-subtle)', fontWeight: 600, fontSize: 13, cursor: manualSerial2.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Look up</button>
                </div>
                <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 10 }}>or scan with camera</div>
                <div style={{ borderRadius: 14, overflow: 'hidden', background: '#111', border: '1px solid var(--border-default)' }}>
                  <PhoneScannerScreen onScan={handleStep2Scan} compact />
                </div>
              </div>
            )}

            {replacementDevice && (
              <>
                <div style={{ background: 'rgba(255,83,13,0.15)', border: '1px solid rgba(255,83,13,0.35)', borderRadius: 14, padding: 14, marginBottom: 16 }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#ff530d', fontFamily: isAnonymous ? 'monospace' : 'inherit' }}>{isAnonymous ? 'New device scanned' : replacementDevice.serial}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-default)', marginLeft: 8 }}>{replacementDevice.type}</span>
                </div>

                <div style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid #60a5fa', borderRadius: 10, padding: 12, fontSize: 12, color: '#60a5fa', marginBottom: 16 }}>
                  <strong style={{ display: 'block', color: 'var(--text-default)', marginBottom: 2 }}>Carrying forward</strong>
                  Operative, phone, and role will be copied from the returning allocation.
                </div>

                <button onClick={handleConfirm} style={{ width: '100%', padding: 13, borderRadius: 10, border: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#ff530d', color: '#fff' }}>
                  Confirm swap
                </button>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={onBack} style={{ flex: 1, padding: 13, borderRadius: 10, border: '1px solid var(--border-default)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--surface-inset)', color: 'var(--text-default)' }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
